import { axiosRequest, METHOD } from '@/api/default.js';
import constants from '@/constants';

/**
 * Get paginated list of corporate requests
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Items per page (1-500, default 100)
 * @param {number} params.offset - Items to skip (default 0)
 * @param {string} params.sort - Sort field:direction (default 'created_at:desc')
 * @param {string} params.status_filter - Filter by status (optional)
 * @param {string} params.search - Search query (optional)
 * @returns {Promise<Object>} { data, pagination, links }
 */
export async function getCorporateRequests(params = {}) {
    const url = constants.URL.CORPORATE_REQUESTS;
    const response = await axiosRequest({
        url,
        method: METHOD.GET,
        requestName: constants.API_REQUEST_NAMES.GET_CORPORATE_REQUESTS,
        params,
        isLoader: false,
    });
    return response == null
        ? {
              data: [],
              pagination: {
                  total: 0,
                  limit: constants.PAGINATION.DEFAULT_LIMIT,
                  offset: 0,
                  count: 0,
              },
              links: {
                  self: null,
                  first: null,
                  prev: null,
                  next: null,
                  last: null,
              },
          }
        : response;
}

/**
 * Get single corporate request by ID
 * @param {string} requestId - Request UUID
 * @returns {Promise<Object>} CorporateRequest object
 */
export async function getCorporateRequestById(requestId) {
    const url = constants.URL.CORPORATE_REQUEST_BY_ID.replace(
        '{request_id}',
        requestId
    );
    const response = await axiosRequest({
        url,
        method: METHOD.GET,
        requestName: constants.API_REQUEST_NAMES.GET_CORPORATE_REQUEST,
        isLoader: false,
    });
    return response;
}

/**
 * Update corporate request status with optional admin notes
 * @param {string} requestId - Request UUID
 * @param {Object} data - Update payload
 * @param {string} data.status - New status (pending|contacted|converted|rejected)
 * @param {string} data.admin_notes - Internal notes (max 2000 chars, optional)
 * @returns {Promise<Object>} Updated CorporateRequest object
 */
export async function updateCorporateRequestStatus(requestId, data) {
    const url = constants.URL.CORPORATE_REQUEST_UPDATE_STATUS.replace(
        '{request_id}',
        requestId
    );
    const response = await axiosRequest({
        url,
        method: METHOD.PATCH,
        requestName:
            constants.API_REQUEST_NAMES.UPDATE_CORPORATE_REQUEST_STATUS,
        data,
        isLoader: true,
    });
    return response;
}

/**
 * Get count of pending corporate requests (for badge)
 * @returns {Promise<number>} Count of pending requests
 */
export async function getPendingRequestsCount() {
    const response = await getCorporateRequests({
        status_filter: constants.CORPORATE_REQUEST_STATUS.PENDING,
        limit: 1,
        offset: 0,
    });
    return response?.pagination?.total ?? 0;
}
